import React, { useRef } from 'react';
import { Alert, Button } from 'antd';
import './async-content-boundary.css';

const FAILURE_STYLE = Object.freeze({ margin: '12px 16px', flexShrink: 0 });

/**
 * @typedef {object} AsyncContentProjection
 * @property {boolean} [active]
 * @property {string} [feedback]
 * @property {string} [interaction]
 * @property {string} [label]
 * @property {string} [operationKey]
 * @property {string} [presentation]
 * @property {number|string} [requestId]
 * @property {object|null} [error]
 * @property {() => unknown} [onRetry]
 */
/** @type {Readonly<AsyncContentProjection>} */
const EMPTY_PROJECTION = Object.freeze({
  active: false,
  feedback: '',
  interaction: '',
  label: '',
  operationKey: '',
  presentation: '',
  requestId: 0,
});

const hasContent = (value) => value !== null && value !== undefined && value !== false;
const selectAcceptedContent = ({ children, pending, retainedContent }) => (
  pending && hasContent(retainedContent) ? retainedContent : children
);
const selectFramePresentation = ({ content, pending, presentation }) => {
  if (!pending) return '';
  if (presentation !== 'retain') return 'skeleton';
  return hasContent(content) ? 'retain' : 'skeleton';
};
const requestedPresentation = (projection) => (
  projection.presentation === 'retain' ? 'retain' : 'skeleton'
);
const resolveFeedbackState = (projection) => {
  if (projection.feedback === 'silent') {
    return { feedback: 'silent', label: undefined, role: undefined };
  }
  return { feedback: 'boundary', label: String(projection.label ?? ''), role: 'status' };
};

export const resolveAsyncContentFrame = ({
  children,
  pending,
  presentation,
  retainedContent,
}) => {
  const acceptedContent = selectAcceptedContent({ children, pending, retainedContent });
  const resolvedPresentation = selectFramePresentation({
    content: acceptedContent,
    pending,
    presentation,
  });
  return {
    content: resolvedPresentation === 'retain' ? acceptedContent : children,
    presentation: resolvedPresentation,
    retainedContent: !pending && hasContent(children) ? children : retainedContent,
  };
};

const resolveBoundaryState = (projection, resolvedPresentation) => {
  const pending = projection.active === true;
  if (!pending) {
    return {
      busy: undefined,
      className: 'flare-async-content-boundary is-settled',
      contentHidden: undefined,
      contentInert: undefined,
      feedback: '',
      label: undefined,
      operationKey: undefined,
      presentation: '',
      requestId: undefined,
      role: undefined,
    };
  }
  const presentation = resolvedPresentation === 'retain' ? 'retain' : 'skeleton';
  const presentationClassName = presentation === 'retain' ? 'is-retained' : 'is-skeleton';
  const interactionBlocked = projection.interaction === 'blocked';
  const interactionClassName = interactionBlocked ? ' is-interaction-blocked' : '';
  return {
    busy: true,
    className: `flare-async-content-boundary is-pending ${presentationClassName}${interactionClassName}`,
    contentHidden: [presentation === 'skeleton', interactionBlocked].some(Boolean) ? true : undefined,
    contentInert: interactionBlocked ? '' : undefined,
    ...resolveFeedbackState(projection),
    operationKey: projection.operationKey,
    presentation,
    requestId: projection.requestId || undefined,
  };
};

const OptionalSlot = ({ children, className }) => {
  if (!children) return null;
  return <span className={className}>{children}</span>;
};

const PendingProgress = ({ label }) => (
  <>
    <OptionalSlot className="flare-async-content-boundary-label">{label}</OptionalSlot>
    <span className="flare-async-content-boundary-track">
      <span className="flare-async-content-boundary-progress" />
    </span>
  </>
);

const PendingOverlay = ({ boundary, fallback, heading, indicator }) => {
  if (boundary.feedback === 'silent') return null;
  if (boundary.presentation === 'skeleton' && fallback) return <>{fallback}<PendingProgress label={boundary.label} /></>;
  return (
    <>
      {boundary.presentation === 'skeleton' ? (
        <>
          <OptionalSlot className="flare-async-content-boundary-indicator">{indicator}</OptionalSlot>
          <OptionalSlot className="flare-async-content-boundary-heading">{heading}</OptionalSlot>
        </>
      ) : null}
      <PendingProgress label={boundary.label} />
    </>
  );
};

/** @param {{ children?: import('react').ReactNode, fallback?: import('react').ReactNode,
 * heading?: string, indicator?: import('react').ReactNode,
 * projection?: Readonly<AsyncContentProjection>, style?: import('react').CSSProperties }} props */
export default function AsyncContentBoundary({
  children,
  fallback = null,
  heading = '',
  indicator = null,
  projection = EMPTY_PROJECTION,
  style,
}) {
  const retainedContentRef = useRef(null);
  const pending = projection.active === true;
  const frame = resolveAsyncContentFrame({
    children,
    pending,
    presentation: requestedPresentation(projection),
    retainedContent: retainedContentRef.current,
  });
  retainedContentRef.current = frame.retainedContent;
  const boundary = resolveBoundaryState(projection, frame.presentation);
  const retryAction = projection.onRetry ? <Button aria-label="重试" onClick={projection.onRetry}>重试</Button> : null;
  return (
    <div
      aria-busy={boundary.busy}
      aria-label={boundary.label}
      className={boundary.className}
      data-operation-key={boundary.operationKey}
      data-request-id={boundary.requestId}
      role={boundary.role}
      style={style}
    >
      <div
        aria-hidden={boundary.contentHidden}
        className="flare-async-content-boundary-content"
        inert={boundary.contentInert}
      >
        {projection.error ? <Alert role="alert" type="error" showIcon title={projection.label}
          action={retryAction} style={FAILURE_STYLE} /> : null}
        {frame.content}
      </div>
      <div aria-hidden="true" className="flare-async-content-boundary-overlay">
        <PendingOverlay boundary={boundary} fallback={fallback} heading={heading} indicator={indicator} />
      </div>
    </div>
  );
}
